import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { displayError, normalizeAxiosError } from '@/lib/error';
import { isAdminOrSupervisor } from '@/services/userService';
import { useHIWorkOrderByNumber } from '@/services/homeInspectionService';
import { getAttempt } from '@/services/homeInspectionAttemptService';
import { useUserStore } from '@/stores/user';
import {
  HI_ATTEMPT_STEP_SCHEMAS,
  type HiAttemptFormValues,
  type HiAttemptGetResponse,
} from './hiAttempt.schema';
import { isWithinEditableWindow, buildDefaultValues, buildAttemptImagePreviews } from './hiAttemptHelpers';
import { HiAttemptStep1OwnerInfo } from './steps/HiAttemptStep1OwnerInfo';
import { HiAttemptStep2TenantInfo } from './steps/HiAttemptStep2TenantInfo';
import { HiAttemptStep3PropertyInfo, type PreviewEntry } from './steps/HiAttemptStep3PropertyInfo';
import { HiAttemptStep4Comments } from './steps/HiAttemptStep4Comments';

const STEPS = [
  { title: 'Owner Information', isCompleted: true },
  { title: 'Tenant Information', isCompleted: true },
  { title: 'Property Information', isCompleted: true },
  { title: 'Comments', isCompleted: true },
];

const noopSetPreviews: Dispatch<SetStateAction<PreviewEntry[]>> = () => undefined;

export const HiAttemptView = () => {
  const { id: workOrderNumber, attemptId } = useParams<{ id: string; attemptId: string }>();
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);

  const [activeStep, setActiveStep] = useState(0);
  const [loadedAttempt, setLoadedAttempt] = useState<HiAttemptGetResponse | null>(null);
  const [isLoadingAttempt, setIsLoadingAttempt] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const { data: workOrder, isFetching: isFetchingWO, isError: isErrorWO } =
    useHIWorkOrderByNumber(workOrderNumber ?? null);

  const canEdit = useMemo(() => {
    if (!loggedInUser || !loadedAttempt) return false;
    if (isAdminOrSupervisor(loggedInUser)) return true;
    if (loggedInUser.teams.some((t) => t.key === 'SERVICE')) {
      return isWithinEditableWindow(loadedAttempt);
    }
    return false;
  }, [loggedInUser, loadedAttempt]);

  const form = useForm<HiAttemptFormValues>({
    resolver: zodResolver(HI_ATTEMPT_STEP_SCHEMAS[activeStep]),
    mode: 'all',
    defaultValues: { mediaIds: [], tenants: [] },
  });

  useEffect(() => {
    const workOrderId = workOrder?.workOrderId;
    if (!workOrderId || !attemptId) return;
    setIsLoadingAttempt(true);
    getAttempt(workOrderId, attemptId)
      .then((data) => {
        setLoadedAttempt(data);
        form.reset(buildDefaultValues(data) as HiAttemptFormValues);
      })
      .catch((err) => {
        displayError(normalizeAxiosError(err), 5000);
        setLoadError(true);
      })
      .finally(() => setIsLoadingAttempt(false));
  }, [workOrder?.workOrderId, attemptId]);

  const initialPreviews = useMemo(
    () => (loadedAttempt ? buildAttemptImagePreviews(loadedAttempt) : []),
    [loadedAttempt]
  );

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    scrollToTop();
  };

  const nextStep = () => {
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    scrollToTop();
  };

  const goToStep = (step: number) => {
    setActiveStep(step);
    scrollToTop();
  };

  if (isFetchingWO || isLoadingAttempt) {
    return (
      <div className="flex items-center justify-center h-64 text-placeholder text-sm">
        Loading…
      </div>
    );
  }

  if (isErrorWO || loadError || !workOrder || !loadedAttempt) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load attempt. Please go back and try again.
      </div>
    );
  }

  return (
    <div className="w-full scrollbar-hide flex flex-col">
      <div className="w-full flex flex-row justify-between">
        <ToolBar title={`View Attempt — W/O #${workOrder.workOrderNumber}`} />
        {canEdit && (
          <Button
            className="h-[36px] p-3 w-fit"
            onClick={() =>
              navigate(`/home-inspection/${workOrderNumber}/attempt/${attemptId}/edit`)
            }
          >
            Edit Status Update
          </Button>
        )}
      </div>
      <div className="mt-3 flex justify-center pl-24">
        <Stepper steps={STEPS} activeStep={activeStep} goToStep={goToStep} />
      </div>

      <FormProvider {...form}>
        {activeStep === 0 && <HiAttemptStep1OwnerInfo workOrder={workOrder} readOnly />}
        {activeStep === 1 && <HiAttemptStep2TenantInfo readOnly />}
        {activeStep === 2 && (
          <HiAttemptStep3PropertyInfo previews={initialPreviews} setPreviews={noopSetPreviews} readOnly />
        )}
        {activeStep === 3 && <HiAttemptStep4Comments workOrder={workOrder} readOnly />}
      </FormProvider>

      <div className="flex justify-end mt-4 mb-8 w-full gap-2">
        {activeStep > 0 && (
          <Button
            variant="outline"
            className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
            onClick={prevStep}
          >
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 && (
          <Button
            className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
            onClick={nextStep}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};
